/**
 * API-client voor Zoea Sites.
 *
 * Hergebruikt de globale axios-instantie die AuthContext configureert
 * (Authorization-header, refresh-op-401, cookies) -- geen eigen instantie,
 * zelfde conventie als `modules/sprekers/api.ts`.
 */
import axios from 'axios';
import type { SiteRenderSnapshot } from '@famfen77/site-renderer';

import type {
  BuilderDraft,
  DevelopmentSiteSession,
  SitePage,
  WebsiteDetail,
  WebsiteSummary,
  SiteChrome,
  SiteMedia,
  DeveloperDocument,
  DomainBinding,
} from './types';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/websites`;
const PUBLIC_MEDIA_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/public/sites`;

/** HTTP 409 op een opslag: iemand anders heeft dit concept ondertussen gewijzigd. */
export class DraftConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DraftConflictError';
  }
}

/** De website heeft (nog) geen bruikbaar developmentadres. */
export class DevelopmentSiteUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DevelopmentSiteUnavailableError';
  }
}

export type BuilderCanonicalRenderSnapshot = SiteRenderSnapshot & {
  sourceRevision: number;
};

export class ProductionPreviewUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProductionPreviewUnavailableError';
  }
}

function messageOf(error: unknown, fallback: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const structured = detail as { message?: unknown; issues?: unknown };
    const summary = typeof structured.message === 'string' && structured.message.trim()
      ? structured.message
      : null;
    const issues = Array.isArray(structured.issues)
      ? structured.issues.map(formatPreviewIssue).filter((item): item is string => Boolean(item))
      : [];
    if (summary || issues.length) return [summary, ...issues].filter(Boolean).join('\n');
  }
  if (Array.isArray(detail)) {
    const messages = detail.map((item) => {
      if (typeof item === 'string') return item;
      const previewIssue = formatPreviewIssue(item);
      if (previewIssue) return previewIssue;
      if (item && typeof item === 'object' && 'msg' in item) {
        const location = 'loc' in item && Array.isArray(item.loc) ? ` (${item.loc.join('.')})` : '';
        return `${String(item.msg)}${location}`;
      }
      return null;
    }).filter((item): item is string => Boolean(item));
    if (messages.length) return messages.join(' · ');
  }
  return fallback;
}

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

function formatPreviewIssue(issue: unknown): string | null {
  if (typeof issue === 'string' && issue.trim()) return issue;
  if (!issue || typeof issue !== 'object') return null;
  const value = issue as {
    message?: unknown;
    msg?: unknown;
    pageTitle?: unknown;
    pageId?: unknown;
    blockId?: unknown;
    blockType?: unknown;
    pages?: unknown;
  };
  const message = typeof value.message === 'string'
    ? value.message
    : typeof value.msg === 'string' ? value.msg : null;
  if (!message?.trim()) return null;

  const pageLabel = (page: unknown): string | null => {
    if (!page || typeof page !== 'object') return null;
    const pageValue = page as { pageTitle?: unknown; pageId?: unknown };
    if (typeof pageValue.pageTitle === 'string' && pageValue.pageTitle.trim()) return `Pagina "${pageValue.pageTitle}"`;
    if (typeof pageValue.pageId === 'string' && pageValue.pageId.trim()) return `Pagina ${pageValue.pageId}`;
    return null;
  };
  const pages = Array.isArray(value.pages)
    ? value.pages.map(pageLabel).filter((label): label is string => Boolean(label))
    : [];
  const context = pages.length
    ? ` (${pages.join(', ')})`
    : pageLabel(value)
      ? ` (${pageLabel(value)})`
      : '';
  const block = typeof value.blockType === 'string' && !message.includes(`"${value.blockType}"`)
    ? ` Bloktype "${value.blockType}"${typeof value.blockId === 'string' ? ` (${value.blockId})` : ''}.`
    : typeof value.blockId === 'string' ? ` (blok ${value.blockId})` : '';
  return `• ${message}${context}${block}`;
}

function v2Pages(pages: SitePage[]): SitePage[] {
  return pages.map((page) => ({
    ...page,
    // Sections are the canonical write shape. The flat projection is only
    // sent for legacy pages because the backend's legacy block union is
    // intentionally narrower than the V3 element union.
    blocks: page.sections?.length ? [] : page.blocks,
    sections: page.sections?.map((section) => ({
      ...section,
      columns: section.columns.map((column) => ({
        ...column,
        blocks: column.blocks.map((block) => block.type === 'paragraph'
          ? { ...block, type: 'rich_text' as const }
          : block),
      })),
    })),
  }));
}

export const sitesApi = {
  mediaUrl: (websiteId: string, mediaId: string): string => `${PUBLIC_MEDIA_BASE}/${encodeURIComponent(websiteId)}/media/${encodeURIComponent(mediaId)}`,

  listMedia: (websiteId: string): Promise<SiteMedia[]> =>
    axios.get<SiteMedia[]>(`${BASE}/${websiteId}/media`).then((response) => response.data),

  uploadMedia: (websiteId: string, file: File): Promise<SiteMedia> => {
    const body = new FormData();
    body.append('file', file);
    return axios.post<SiteMedia>(`${BASE}/${websiteId}/media`, body).then((response) => response.data);
  },

  importMediaUrl: (websiteId: string, url: string, filename?: string): Promise<SiteMedia> =>
    axios.post<SiteMedia>(`${BASE}/${websiteId}/media/import-url`, { url, ...(filename ? { filename } : {}) }).then((response) => response.data),

  updateMedia: (websiteId: string, mediaId: string, patch: Partial<Pick<SiteMedia, 'alt_text' | 'focal_x' | 'focal_y'>>): Promise<SiteMedia> =>
    axios.patch<SiteMedia>(`${BASE}/${websiteId}/media/${encodeURIComponent(mediaId)}`, patch).then((response) => response.data),

  deleteMedia: (websiteId: string, mediaId: string): Promise<void> =>
    axios.delete(`${BASE}/${websiteId}/media/${encodeURIComponent(mediaId)}`).then(() => undefined),

  listWebsites: (): Promise<WebsiteSummary[]> =>
    axios.get<WebsiteSummary[]>(BASE).then((response) => response.data),

  createWebsite: (name: string): Promise<WebsiteDetail> =>
    axios.post<WebsiteDetail>(BASE, { name }).then((response) => response.data),

  getWebsite: (websiteId: string): Promise<WebsiteDetail> =>
    axios.get<WebsiteDetail>(`${BASE}/${websiteId}`).then((response) => response.data),

  listDomainBindings: (websiteId: string): Promise<DomainBinding[]> =>
    axios.get<DomainBinding[]>(`${BASE}/${websiteId}/domains`).then((response) => response.data),

  createDomainBinding: (websiteId: string, hostname: string): Promise<DomainBinding> =>
    axios.post<DomainBinding>(`${BASE}/${websiteId}/domains`, {
      hostname,
      environment: 'development',
      isPrimary: true,
    }).then((response) => response.data),

  renameWebsite: (websiteId: string, name: string): Promise<WebsiteDetail> =>
    axios.patch<WebsiteDetail>(`${BASE}/${websiteId}`, { name }).then((response) => response.data),

  deleteWebsite: (websiteId: string): Promise<void> =>
    axios.delete(`${BASE}/${websiteId}`).then(() => undefined),

  getDraft: (websiteId: string): Promise<BuilderDraft> =>
    axios
      .get<BuilderDraft>(`${BASE}/${websiteId}/builder/draft`)
      .then((response) => response.data),

  /**
   * Canonical, render-safe projection of the CURRENT editor state.
   * This is deliberately non-persistent and uses the same backend mapper as
   * immutable Preview/Public. The Builder therefore no longer needs to
   * invent a second visual/content interpretation.
   */
  renderDraft: (
    websiteId: string,
    revision: number,
    pages: SitePage[],
    site: SiteChrome,
  ): Promise<BuilderCanonicalRenderSnapshot> =>
    axios
      .post<BuilderCanonicalRenderSnapshot>(
        `${BASE}/${websiteId}/builder/render-snapshot`,
        { revision, contentSchemaVersion: 3, pages: v2Pages(pages), site },
      )
      .then((response) => response.data),

  getDeveloperDocument: (websiteId: string, pageId: string): Promise<DeveloperDocument> =>
    axios.get<DeveloperDocument>(`${BASE}/${websiteId}/builder/developer/${encodeURIComponent(pageId)}`).then((response) => response.data),

  applyDeveloperDocument: async (
    websiteId: string,
    pageId: string,
    revision: number,
    html: string,
    css: string,
    pageCss: string,
  ): Promise<{ document: DeveloperDocument; draft: BuilderDraft }> => {
    try {
      const response = await axios.put<{ document: DeveloperDocument; draft: BuilderDraft }>(
        `${BASE}/${websiteId}/builder/developer/${encodeURIComponent(pageId)}`,
        { pageId, revision, html, css, pageCss },
      );
      return response.data;
    } catch (error) {
      if (statusOf(error) === 409) {
        throw new DraftConflictError(messageOf(error, 'Deze website is ondertussen door iemand anders gewijzigd.'));
      }
      throw error;
    }
  },

  /**
   * Slaat het concept op onder de revisie die de bouwer denkt te hebben.
   * De server accepteert de schrijfactie alleen zolang dat nog de actuele
   * revisie is -- een 409 betekent dus altijd "iemand anders was eerder",
   * nooit "probeer het gewoon nog eens".
   */
  saveDraft: async (
    websiteId: string,
    revision: number,
    pages: SitePage[],
    site?: SiteChrome,
  ): Promise<BuilderDraft> => {
    try {
      const response = await axios.put<BuilderDraft>(
        `${BASE}/${websiteId}/builder/draft`,
        { revision, contentSchemaVersion: 3, pages: v2Pages(pages), ...(site ? { site } : {}) },
      );
      return response.data;
    } catch (error) {
      if (statusOf(error) === 409) {
        throw new DraftConflictError(
          messageOf(error, 'Deze website is ondertussen door iemand anders gewijzigd.'),
        );
      }
      throw error;
    }
  },

  createPreview: async (websiteId: string, expectedDraftRevision: number) => {
    try {
      const response = await axios.post<{ previewId: string; websiteId: string; sourceDraftRevision: number }>(
        `${BASE}/${websiteId}/previews`,
        { expectedDraftRevision },
      );
      return response.data;
    } catch (error) {
      if (statusOf(error) === 409) {
        throw new DraftConflictError(
          messageOf(error, 'Het concept is ondertussen gewijzigd. Herlaad en probeer opnieuw.'),
        );
      }
      throw error;
    }
  },

  issuePreviewGrant: async (websiteId: string, previewId: string): Promise<{ grantId: string; shareUrl: string; expiresAt: string }> => {
    try {
      const response = await axios.post<{ grantId: string; shareUrl: string; expiresAt: string }>(
        `${BASE}/${websiteId}/preview-grants`,
        { previewId },
      );
      return response.data;
    } catch (error) {
      if (statusOf(error) === 503) {
        throw new ProductionPreviewUnavailableError(
          messageOf(error, 'Production preview is nog niet geconfigureerd.'),
        );
      }
      throw error;
    }
  },

  /** Internal developer tooling only. Never use this from the normal Preview action. */
  openDevelopmentSite: async (websiteId: string): Promise<DevelopmentSiteSession> => {
    const response = await axios.post<DevelopmentSiteSession>(
      `${BASE}/${websiteId}/development-access/session`,
    );
    return response.data;
  },

  createProductionPreview: async (websiteId: string, expectedDraftRevision: number) => {
    const preview = await sitesApi.createPreview(websiteId, expectedDraftRevision);
    return sitesApi.issuePreviewGrant(websiteId, preview.previewId);
  },
};

export { messageOf as apiErrorMessage };
